import React, { useMemo, useState } from 'react';
import {
    Paper,
    Box,
    Typography,
    Table,
    TableContainer,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    Grid,
    makeStyles,
    Collapse,
    IconButton
} from '@material-ui/core';
import { InfoOutlinedIcon as InfoIcon } from '../../icons';
import { formatMoney } from '../../utils';
import moment from '../../moment-hawaii';
import ReadonlyEditor from '../../components/drafteditor/components/ReadonlyEditor';

const useStyles = makeStyles(() => ({
    headerBar: {
        background: '#0b1723',
        color: '#fff',
        padding: '14px 20px',
        borderBottom: '1px solid #ddd'
    },
    headCell: {
        fontWeight: 700,
        textAlign: 'left',
        borderRight: '1px solid #e0e0e0'
    },
    row: {
        '&:nth-child(odd)': { backgroundColor: '#ffffff' },
        '&:nth-child(even)': { backgroundColor: '#f9fbff' }
    },
    cell: { borderRight: '1px solid #e0e0e0', verticalAlign: 'top' },
    dateCell: { whiteSpace: 'nowrap', fontWeight: 600 },
    amountCell: { fontWeight: 700, textAlign: 'right', whiteSpace: 'nowrap' },
    summaryKey: { borderTop: '5px solid #e5e7eb' },
    summaryVal: { borderTop: '5px solid #e5e7eb', textAlign: 'right', fontWeight: 700 },
    totalWrap: { padding: 20 },
    totalText: { fontWeight: 800, fontSize: 22, color: '#0b1723' },
    smallMuted: { color: '#6b7280', fontSize: 13 },
    infoButton: {
        padding: 2,
        marginLeft: 2,
        color: '#6b7280',
        transition: 'all 0.2s',
        verticalAlign: 'super',
        fontSize: '0.7rem',
        '&:hover': {
            color: '#0b1723',
            backgroundColor: 'rgba(0, 0, 0, 0.04)'
        },
        '&.active': {
            color: '#1976d2',
            backgroundColor: 'rgba(25, 118, 210, 0.08)'
        }
    },
    descriptionCell: {
        paddingTop: 0,
        paddingBottom: 12,
        fontSize: '0.875rem',
        color: '#6b7280',
        whiteSpace: 'pre-wrap',
        '@media print': {
            display: 'table-cell !important'
        }
    },
    descriptionRow: {
        backgroundColor: '#f8fafc !important'
    },
    tableFixed: {
        tableLayout: 'fixed',
        '& th:nth-child(1)': { width: '130px' },
        '& th:nth-child(2)': { width: '80px' },
        '& th:nth-child(3)': { width: 'auto' },
        '& th:nth-child(4)': { width: '180px' },
        '& th:nth-child(5)': { width: '30px' },
        '& th:nth-child(6)': { width: '90px' }
    }
}));

const TABLE_HEADERS = ['Date', 'Time', 'Activity', 'Company', 'Qty', 'Amount'];

const ItineraryRow = ({ booking, idx, classes }) => {
    const [expanded, setExpanded] = useState(false);

    const start = booking.activity_start ? moment.utc(booking.activity_start) : null;
    const date = booking.date ?? (start ? start.format('ddd, MMM DD YYYY') : '');
    const time = booking.time ?? (start ? start.format('h:mm a') : '');
    const activityName =
        typeof booking.activity === 'string' ? booking.activity : booking.activity?.name ?? '';
    const companyName = booking.company ?? booking.vendor ?? booking.activity?.company?.name ?? '';
    const qty =
        typeof booking.quantity === 'number'
            ? booking.quantity
            : Array.isArray(booking.tickets)
            ? booking.tickets.reduce((acc, t) => acc + (t.quantity || 0), 0)
            : 0;
    const amount = booking.subtotal ?? booking.amount;
    // Handle description from different data sources
    let activityDescription = '';
    if (typeof booking.activity !== 'string' && booking.activity?.description) {
        activityDescription = booking.activity.description;
    }

    // there is a case where description is passed directly in booking
    if (!activityDescription && booking.description) {
        activityDescription = booking.description;
    }

    // Check if description has actual content
    let hasContent = false;
    if (activityDescription) {
        try {
            const parsed =
                typeof activityDescription === 'string'
                    ? JSON.parse(activityDescription)
                    : activityDescription;
            if (parsed && parsed.blocks && Array.isArray(parsed.blocks)) {
                hasContent = parsed.blocks.some(
                    block => block.text && block.text.trim().length > 0
                );
            }
        } catch (e) {
            // If it's not JSON, assume it's HTML and check for content
            hasContent = activityDescription.trim().replace(/<[^>]*>/g, '').length > 0;
        }
    }

    return (
        <>
            <TableRow key={idx} className={classes.row}>
                <TableCell className={`${classes.cell} ${classes.dateCell}`}>{date}</TableCell>
                <TableCell className={classes.cell}>{time}</TableCell>
                <TableCell
                    className={classes.cell}
                    style={{ maxWidth: 280, whiteSpace: 'pre-wrap' }}
                >
                    <Box display="inline">
                        {activityName}
                        {hasContent && (
                            <IconButton
                                size="small"
                                onClick={() => setExpanded(!expanded)}
                                className={`${classes.infoButton} ${
                                    expanded ? 'active' : ''
                                } hide-in-print`}
                                title={expanded ? 'Hide description' : 'Show description'}
                                style={{ verticalAlign: 'super', padding: 2, marginLeft: 2 }}
                            >
                                <InfoIcon style={{ fontSize: '0.9rem' }} />
                            </IconButton>
                        )}
                    </Box>
                </TableCell>
                <TableCell
                    className={classes.cell}
                    style={{ maxWidth: 240, whiteSpace: 'pre-wrap' }}
                >
                    {companyName}
                </TableCell>
                <TableCell className={classes.cell}>{qty}</TableCell>
                <TableCell className={classes.amountCell}>{formatMoney(amount)}</TableCell>
            </TableRow>
            {hasContent && (
                <TableRow key={`${idx}-desc`} className={`${classes.descriptionRow} hide-in-print`}>
                    <TableCell
                        colSpan={6}
                        className={classes.cell}
                        style={{ padding: 0, border: 'none' }}
                    >
                        <Collapse in={expanded} timeout="auto" unmountOnExit>
                            <Box
                                className={classes.descriptionCell}
                                style={{ padding: '12px 16px', borderLeft: '3px solid #1976d2' }}
                            >
                                <ReadonlyEditor value={activityDescription} />
                            </Box>
                        </Collapse>
                    </TableCell>
                </TableRow>
            )}
            {hasContent && (
                <TableRow
                    key={`${idx}-desc-print`}
                    className="show-in-print"
                    style={{ display: 'none' }}
                >
                    <TableCell colSpan={6} className={classes.descriptionCell}>
                        <ReadonlyEditor value={activityDescription} />
                    </TableCell>
                </TableRow>
            )}
        </>
    );
};

const ItineraryTable = ({
    itemsToBook = [],
    bookingItemsCount = 0,
    grandTotal,
    taxesAndFees,
    discount,
    showFeeAndActivity
}) => {
    const classes = useStyles();

    const computedGrand = useMemo(
        () => itemsToBook.reduce((acc, b) => acc + (b.subtotal ?? b.amount), 0),
        [itemsToBook]
    );

    const activitiesTotal = grandTotal ?? computedGrand;
    const total = activitiesTotal + (taxesAndFees ?? 0);

    return (
        <>
            <Paper elevation={3} style={{ marginBottom: 20, overflow: 'hidden', borderRadius: 8 }}>
                <Box className={classes.headerBar}>
                    <Typography
                        variant="h6"
                        style={{
                            fontWeight: 800,
                            fontSize: 18,
                            display: 'flex',
                            justifyContent: 'center'
                        }}
                    >
                        Scheduled activities
                    </Typography>
                </Box>
                <TableContainer>
                    <Table className={classes.tableFixed}>
                        <TableHead>
                            <TableRow style={{ backgroundColor: '#f8fafc' }}>
                                {TABLE_HEADERS.map((header, index) => (
                                    <TableCell
                                        key={header}
                                        className={classes.headCell}
                                        style={{
                                            borderRight:
                                                index < TABLE_HEADERS.length - 1
                                                    ? '1px solid #e0e0e0'
                                                    : 'none'
                                        }}
                                    >
                                        {header}
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>

                        <TableBody>
                            {itemsToBook.map((booking, idx) => (
                                <ItineraryRow
                                    key={idx}
                                    booking={booking}
                                    idx={idx}
                                    classes={classes}
                                />
                            ))}

                            {showFeeAndActivity && (
                                <>
                                    <TableRow>
                                        <TableCell colSpan={5} className={classes.summaryKey}>
                                            Activities
                                        </TableCell>
                                        <TableCell className={classes.summaryVal}>
                                            {formatMoney(activitiesTotal)}
                                        </TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell colSpan={5}>Taxes and fees</TableCell>
                                        <TableCell className={classes.amountCell}>
                                            {formatMoney(taxesAndFees)}
                                        </TableCell>
                                    </TableRow>
                                </>
                            )}

                            {discount > 0 && (
                                <TableRow>
                                    <TableCell colSpan={5} className={classes.summaryKey}>
                                        Discount
                                    </TableCell>
                                    <TableCell className={classes.summaryVal}>
                                        -{formatMoney(discount)}
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>
            <Paper elevation={2} className={classes.totalWrap}>
                <Grid container alignItems="center">
                    <Grid item xs={12} sm={6}>
                        <Typography style={{ fontWeight: 700 }}>Total</Typography>
                        <Typography className={classes.smallMuted}>
                            {bookingItemsCount} activit{bookingItemsCount !== 1 ? 'ies' : 'y'}{' '}
                            scheduled
                        </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} style={{ textAlign: 'right' }}>
                        <Typography className={classes.totalText}>{formatMoney(total)}</Typography>
                    </Grid>
                </Grid>
            </Paper>
        </>
    );
};

export default ItineraryTable;
